import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRightIcon } from 'lucide-react';
import { usePageMeta } from '../../hooks/usePageMeta';
import { industries } from '../../data/industries';
import { services } from '../../data/services';
import { jobs } from '../../data/jobs';
import { PageHero } from '../../components/ui/PageHero';
import { CtaBand } from '../../components/ui/CtaBand';
export function Industries() {
  usePageMeta('Industries We Serve', 'Executive search and recruitment across financial services, technology, healthcare, energy, industrial, legal, real estate, government and more.');
  const [active, setActive] = useState(industries[0].slug);
  useEffect(() => {
    const observer = new IntersectionObserver(entries => {
      const visible = entries.filter(e => e.isIntersecting).sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
      if (visible[0]) setActive(visible[0].target.id);
    }, {
      rootMargin: '-30% 0px -60% 0px'
    });
    industries.forEach(i => {
      const el = document.getElementById(i.slug);
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, []);
  return <>
      <PageHero crumbs={[{
      label: 'Industries'
    }]} kicker="Industries we serve" title="Sector depth across twelve industries." intro="Leadership challenges differ by sector. Our consultants bring an understanding of the regulatory, commercial and cultural context behind every role." data-id="element-1144" data-mp="17ui3nqgpnwod" />
      <section className="bg-ivory py-20" data-id="element-1145" data-mp="tye91w1xdmbd">
        <div className="container-page grid gap-12 lg:grid-cols-12" data-id="element-1146" data-mp="owf8etxkl3i4">
          <nav aria-label="Industries" className="lg:col-span-3" data-id="element-1147" data-mp="jy3yykb6sv0f">
            <ul className="flex gap-2 overflow-x-auto pb-2 lg:sticky lg:top-28 lg:flex-col lg:gap-0.5 lg:overflow-visible lg:pb-0" data-id="element-1148" data-mp="jzyjvymgkn4i">
              {industries.map(ind => <li key={ind.slug} className="shrink-0" data-id="element-1149" data-mp="2edgajui5u03r">
                  <a href={`#${ind.slug}`} aria-current={active === ind.slug ? 'true' : undefined} className={`block whitespace-nowrap rounded-md px-3 py-2 text-sm transition-colors duration-150 lg:whitespace-normal ${active === ind.slug ? 'bg-midnight text-white' : 'text-muted hover:bg-white hover:text-ink'}`} data-id="element-1150" data-mp="cmz8uwewlc8p">
                    {ind.name}
                  </a>
                </li>)}
            </ul>
          </nav>
          <div className="space-y-6 lg:col-span-9" data-id="element-1151" data-mp="10c1sg9t3lf5u">
            {industries.map(ind => {
            const Icon = ind.icon;
            const related = services.filter(s => ind.services.includes(s.slug));
            const jobCount = jobs.filter(j => j.industry === ind.slug).length;
            return <article key={ind.slug} id={ind.slug} className="scroll-mt-28 rounded-xl border border-line bg-white p-7 md:p-10" data-id="element-1152" data-mp="367uzqq5h0ccd">
                  <div className="flex items-start gap-4" data-id="element-1153" data-mp="1t6gmm10yxllq">
                    <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-md bg-midnight text-gold-300" data-id="element-1154" data-mp="1t92uk1rru34h">
                      <Icon className="h-6 w-6" aria-hidden="true" data-id="element-1155" data-mp="227ehnttfiis6" />
                    </span>
                    <div data-id="element-1156" data-mp="1y6chnarou2p5">
                      <h2 className="font-display text-3xl font-semibold leading-tight text-ink" data-id="element-1157" data-mp="1y74dmmp762fy">{ind.name}</h2>
                      <p className="mt-2 max-w-2xl leading-relaxed text-muted" data-id="element-1158" data-mp="1aha1k7cpchwr">{ind.intro}</p>
                    </div>
                  </div>
                  <div className="mt-8 grid gap-8 md:grid-cols-3" data-id="element-1159" data-mp="15fkpcodqty73">
                    <div data-id="element-1160" data-mp="1jad9k04kw2k0">
                      <h3 className="text-sm font-medium text-ink" data-id="element-1161" data-mp="1jan4810kixw1">Relevant roles</h3>
                      <ul className="mt-3 space-y-2 text-[15px] text-muted" data-id="element-1162" data-mp="1efzbtr2g1jc6">
                        {ind.roles.map(r => <li key={r} data-id="element-1163" data-mp="mqwaok5usp3r">{r}</li>)}
                      </ul>
                    </div>
                    <div data-id="element-1164" data-mp="zgwtut1hl27a">
                      <h3 className="text-sm font-medium text-ink" data-id="element-1165" data-mp="zhq4i53kgbup">Typical challenges</h3>
                      <ul className="mt-3 space-y-2 text-[15px] text-muted" data-id="element-1166" data-mp="14fzzaeldpafs">
                        {ind.challenges.map(c => <li key={c} className="flex gap-2.5" data-id="element-1167" data-mp="19c8ajgwggjho">
                            <span className="mt-2 h-1.5 w-1.5 shrink-0 rotate-45 bg-gold" aria-hidden="true" data-id="element-1168" data-mp="1e5tc1ix9bvlm" />
                            {c}
                          </li>)}
                      </ul>
                    </div>
                    <div data-id="element-1169" data-mp="1s4szin91a4g7">
                      <h3 className="text-sm font-medium text-ink" data-id="element-1170" data-mp="1s4j7yv8cbp8e">Related services</h3>
                      <ul className="mt-3 space-y-2 text-[15px]" data-id="element-1171" data-mp="1x4fd4xsrxmpj">
                        {related.map(s => <li key={s.slug} data-id="element-1172" data-mp="3twz3rs7mrzkd">
                            <Link to={s.slug === 'executive-search' ? '/executive-search' : `/services#${s.slug}`} className="text-royal hover:text-midnight hover:underline" data-id="element-1173" data-mp="3tya5lmhimx8u">
                              {s.title}
                            </Link>
                          </li>)}
                      </ul>
                    </div>
                  </div>
                  <Link to={`/jobs?industry=${ind.slug}`} className="mt-8 inline-flex items-center gap-2 border-t border-line pt-5 text-sm font-medium text-ink hover:text-royal" data-id="element-1174" data-mp="3hmg2ssu0mngs">
                    {jobCount > 0 ? `View ${jobCount} sample ${jobCount === 1 ? 'opportunity' : 'opportunities'}` : 'Browse opportunities'}
                    <ArrowRightIcon className="h-4 w-4" aria-hidden="true" data-id="element-1175" data-mp="460c9z3i0h5a" />
                  </Link>
                </article>;
          })}
          </div>
        </div>
      </section>
      <CtaBand data-id="element-1176" data-mp="2oybvk2vsq2bz" />
    </>;
}